import React, {createContext, useContext, useReducer} from "react";

const initialState = {
    page: 1,
    assetIds: [],
    assetsLoading: false,
    loadFailed: false,
    loadingMore: false,
    reloads: 0,
    refreshVersion: 0,
    tags: [],
    assetsPerRow: 0,
}

const LoadedAssetsStateContext = createContext(initialState);
const LoadedAssetsDispatchContext = createContext(initialState);

const reducer = (state, action) => {
    if (process.env.NODE_ENV === 'development') {
        console.log('LoadedAssetsContext reducer:', action.type, action);
    }

    let i, assetIds, newAssetId, newAssetIds, tags, newTags;

    switch (action.type) {
        // New Assets Loading:
        case 'assetsLoading':
            return {...state, assetsLoading: true, loadFailed: false, assetIds: []};

        case 'assetsRefreshStarted':
            // A retry of an unsuccessful search still owes a loading state.
            return {...state, assetsLoading: state.assetsLoading || state.loadFailed, loadFailed: false};

        case 'assetsRefreshed':
            // Replace membership/counts only after a background response arrives.
            // No empty intermediate grid, but deleted assets still disappear.
            newAssetIds = new Array(Math.max(action.total || 0, action.assets.length));
            action.assets.forEach((asset, index) => { newAssetIds[index] = asset.id; });
            return {...state, assetsLoading: false, loadFailed: false, assetIds: newAssetIds, refreshVersion: state.refreshVersion + 1};

        case 'assetsLoadFailed':
            return {...state, assetsLoading: false,
                loadFailed: !action.background || state.assetsLoading || state.loadFailed,
                refreshVersion: state.refreshVersion + 1};

        // More Assets Loading (Table):
        case 'moreAssetsLoading':
            return {...state, assetsLoading: true};

        case 'assetsLoaded':
            // Grow-only: never shrink assetIds based on a single response's total.
            // ES total_entries can drift between requests for large result sets
            // (track_total_hits is not enabled on the main result), and
            // out-of-order page responses would otherwise wipe slots already
            // populated by earlier pages — leaving the grid empty mid-scroll.
            // A new search resets via the assetsLoading branch above.
            const targetLength = Math.max(state.assetIds.length, action.total || 0);
            newAssetIds = [...state.assetIds];
            newAssetIds.length = targetLength;

            assetIds = action.assets.map(a => a.id)
            newAssetIds.splice((action.page - 1) * action.per_page, action.assets.length, ...assetIds);

            return {...state, assetsLoading: false, loadFailed: false, assetIds: newAssetIds};

        case 'assetsAdded':
            newTags = _.uniq(_.flatten(action.assets.map(asset => asset.tags)));
            tags = _.uniqBy([...state.tags, ...newTags], 'tag_id')

            return {...state, assetsLoading: false, assetIds: _.uniq([...state.assetIds, ...action.assets.map(a => a.id)]), tags};

        case 'reorderAssets':
            i = action.beforeAssetId ? state.assetIds.indexOf(action.beforeAssetId) : state.assetIds.length - 1
            const reorderedIds = state.assetIds.filter(id => action.movingAssetIds.indexOf(id) == -1)
            reorderedIds.splice(i, 0, ...action.movingAssetIds)
            return {...state, assetIds: reorderedIds};

        case 'reload':
            return {...state, reloads: state.reloads + 1 };

        case 'removeSelectedAssets':
            assetIds = _.filter(state.assetIds, id => action.selectedAssetIds.indexOf(id) == -1)
            return {...state, assetIds: [...assetIds], reloads: state.reloads + 1};

        case 'removeAsset':
            assetIds = _.filter(state.assetIds, id => id != action.id)
            return {...state, assetIds: [...assetIds], reloads: state.reloads + 1};

        case 'setAssetsPerRow':
            return {...state, assetsPerRow: action.assetsPerRow};

        default:
            return state;
    }
}

const LoadedAssetsProvider = ({children})=> {
    const [state, dispatch] = useReducer(reducer, initialState);
    return (
        <LoadedAssetsStateContext.Provider value={state}>
            <LoadedAssetsDispatchContext.Provider value={dispatch}>
                {children}
            </LoadedAssetsDispatchContext.Provider>
        </LoadedAssetsStateContext.Provider>
    )
}

function useLoadedAssetsState() {
    const context = useContext(LoadedAssetsStateContext)
    if (context === undefined) {
        throw new Error('useAssetsState must be used within a LoadedAssetsProvider')
    }
    return context
}

function useLoadedAssetsDispatch() {
    const context = useContext(LoadedAssetsDispatchContext)
    if (context === undefined) {
        throw new Error('useAssetsDispatch must be used within a LoadedAssetsProvider')
    }
    return context
}

export {
    LoadedAssetsProvider,
    useLoadedAssetsState,
    useLoadedAssetsDispatch,
    reducer,
    initialState
};
